/**
 * ChangelogModal — Klickbare Versionsanzeige -> zeigt was sich geaendert hat.
 *
 * Datenquelle: /app/frontend/src/changelog.json (manuell gepflegt)
 * Aufruf: <ChangelogModal open={open} onClose={...} currentLabel={BUILD_LABEL} />
 *
 * Das aktuell deployte Release (= currentLabel) wird oben hervorgehoben.
 */
import { useEffect } from "react";
import { X, Tag, Sparkles, Clock } from "lucide-react";
import changelog from "../changelog.json";

export default function ChangelogModal({ open, onClose, currentLabel }) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-16 px-4 pb-8"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="changelog-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden flex flex-col"
        style={{
          maxHeight: "calc(100vh - 8rem)",
          background: "var(--tga-bg-card)",
          border: "1px solid var(--tga-border-strong)",
          color: "var(--tga-text)",
        }}
        data-testid="changelog-modal"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: "1px solid var(--tga-border)" }}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: "var(--tga-blue)" }} />
            <h2 className="text-[14px] font-bold" style={{ color: "var(--tga-text-bright)" }}>
              Was wurde geändert
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded transition-colors"
            style={{ color: "var(--tga-text-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--tga-bg-hover-soft)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            data-testid="changelog-close-btn"
            aria-label="Schliessen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Aktuelle Version Hint */}
        {currentLabel && (
          <div
            className="px-5 py-2 text-[11px] flex items-center gap-2"
            style={{
              background: "rgba(59,130,246,0.06)",
              borderBottom: "1px solid var(--tga-border)",
              color: "var(--tga-text-secondary)",
            }}
            data-testid="changelog-current-label"
          >
            <Clock className="w-3 h-3" style={{ color: "var(--tga-blue)" }} />
            <span>Aktuell live:</span>
            <span className="font-mono font-bold" style={{ color: "var(--tga-blue)" }}>
              {currentLabel}
            </span>
          </div>
        )}

        {/* Liste */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5" data-testid="changelog-list">
          {changelog.length === 0 && (
            <p className="text-[12px] text-center py-8" style={{ color: "var(--tga-text-muted)" }}>
              Noch keine Eintraege vorhanden.
            </p>
          )}
          {changelog.map((entry, idx) => {
            const isCurrent = entry.version === currentLabel;
            return (
              <div key={`${entry.version}-${idx}`} data-testid={`changelog-entry-${idx}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-3 h-3" style={{ color: isCurrent ? "var(--tga-blue)" : "var(--tga-text-muted)" }} />
                  <span
                    className="font-mono text-[11px] font-bold"
                    style={{ color: isCurrent ? "var(--tga-blue)" : "var(--tga-text-bright)" }}
                  >
                    {entry.version}
                  </span>
                  {isCurrent && (
                    <span
                      className="px-1.5 py-0.5 text-[9px] font-bold rounded uppercase tracking-wider"
                      style={{ background: "rgba(59,130,246,0.15)", color: "var(--tga-blue)" }}
                    >
                      Live
                    </span>
                  )}
                </div>
                <ul className="space-y-1 pl-5">
                  {(entry.changes || []).map((c, i) => (
                    <li
                      key={i}
                      className="text-[12px] leading-relaxed list-disc"
                      style={{ color: "var(--tga-text-secondary)" }}
                    >
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          className="px-5 py-2 text-[10px] flex items-center justify-between"
          style={{
            borderTop: "1px solid var(--tga-border)",
            color: "var(--tga-text-dim)",
            background: "var(--tga-bg-elevated)",
          }}
        >
          <span>{changelog.length} Release{changelog.length === 1 ? "" : "s"} gelistet</span>
          <span>changelog.json &middot; manuell gepflegt</span>
        </div>
      </div>
    </div>
  );
}
